/**
 * Owner: Masa Bayu
 * File: AuthorStorefrontPanel.tsx
 * Date:Time: 2026-09-16 14:25
 * Description: T6-E3 author storefront UI — visibility · price hook · shopper preview.
 */

import { useId, useState } from 'react';
import {
  getDemoStorefrontConfig,
  getDemoStorefrontPreview,
  getDemoStorefrontStatus,
  previewDemoStorefrontAsShopper,
  resetDemoAuthorStorefront,
  saveDemoStorefront,
} from './demo-author-storefront';

export interface AuthorStorefrontPanelProps {
  readonly onNavigate: (path: string) => void;
}

export function AuthorStorefrontPanel({ onNavigate }: AuthorStorefrontPanelProps) {
  const statusId = useId();
  const controlsId = useId();
  const [status, setStatus] = useState(() => getDemoStorefrontStatus());
  const [config, setConfig] = useState(() => getDemoStorefrontConfig());

  const refresh = (nextStatus: string) => {
    setStatus(nextStatus);
    setConfig(getDemoStorefrontConfig());
  };

  const preview = getDemoStorefrontPreview();

  return (
    <section className="route-panel author-storefront" aria-labelledby="author-store-title">
      <h2 id="author-store-title">Author storefront</h2>
      <p>
        Listing visibility and price hooks. Checkout recomputes Offer server-side (T3). Shopper
        preview opens Book Detail read-only — no entitlement · no Reader.
      </p>

      <p id={statusId} role="status" aria-live="polite" className="author-storefront__status">
        {status}
        {config !== null ? ` · profile ${config.publicProfilePath}` : ''}
      </p>

      <div
        id={controlsId}
        role="group"
        aria-label="Author storefront actions"
        className="author-storefront__controls"
      >
        <button
          type="button"
          onClick={() => {
            refresh(saveDemoStorefront('public_listed'));
          }}
        >
          List publicly
        </button>
        <button
          type="button"
          onClick={() => {
            refresh(saveDemoStorefront('unlisted'));
          }}
        >
          Unlisted link
        </button>
        <button
          type="button"
          onClick={() => {
            refresh(saveDemoStorefront('hidden'));
          }}
        >
          Hidden
        </button>
        <button
          type="button"
          onClick={() => {
            refresh(previewDemoStorefrontAsShopper());
          }}
        >
          Preview as shopper
        </button>
        <button
          type="button"
          onClick={() => {
            if (preview !== null) {
              onNavigate(preview.shopperPath);
            }
          }}
          disabled={preview === null}
        >
          Open Book Detail
        </button>
        <button
          type="button"
          onClick={() => {
            resetDemoAuthorStorefront();
            refresh(getDemoStorefrontStatus());
          }}
        >
          Reset
        </button>
        <button
          type="button"
          onClick={() => {
            onNavigate('/author');
          }}
        >
          Dashboard
        </button>
      </div>
    </section>
  );
}
